/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
} from '@angular/core';
import { filter$, tap$ } from '@gh/rx/operators';
import { first, forEachRight, isEmpty, isNil, last, range, uniqueId } from 'lodash';
import { WizardStepDirective } from './wizard-step.directive';

@Component({
  standalone: false,
  selector: 'gh-wizard',
  templateUrl: 'wizard.component.html',
  exportAs: 'ghWizard',
})
export class WizardComponent implements AfterContentInit {
  @Input() start?: string;
  @Input() finishDisabled: boolean = false;

  @Output('finish') finish$$ = new EventEmitter<void>();

  @ContentChildren(WizardStepDirective, { descendants: true }) steps: QueryList<WizardStepDirective>;

  currentStep: WizardStepDirective;

  path: string[] = [];

  get currentStepIndex(): number {
    // path is empty until ngAfterContentInit; without clamp, currentStepIndex + 1 goes 0→1 in the same CD cycle (NG0100).
    return Math.max(0, this.path.length - 1);
  }

  get stepCount(): number {
    return this.steps ? this.steps.length : 0;
  }

  get isNextDisabled(): boolean {
    const nextStepName = this.currentStep.next;
    if (isNil(nextStepName)) {
      return false;
    }

    const nextStep = this.getStep(nextStepName);
    return isNil(nextStep) ? false : nextStep.disabled;
  }

  get isPreviousDisabled(): boolean {
    const previousStepName = last(this.path);
    if (isNil(previousStepName)) {
      return false;
    }

    const previousStep = this.getStep(previousStepName);
    return isNil(previousStep) ? false : previousStep.disabled;
  }

  get isFinishDisabled(): boolean {
    return this.finishDisabled;
  }

  get hasNext(): boolean {
    return !isEmpty(this.currentStep.next);
  }

  get hasPrevious(): boolean {
    return this.path.length > 1;
  }

  get canFinish(): boolean {
    return !this.hasNext && !this.isFinishDisabled;
  }

  ngAfterContentInit(): void {
    this.ensureNames();

    const { start } = this;
    const startStep = start ? this.getStep(start) : this.steps.first;

    if (isNil(startStep)) {
      throw new Error(`WizardComponent: Start step cannot be defined`);
    }

    this.path.push(startStep.name);
    this.setCurrentStep(startStep);
  }

  finish(): void {
    if (this.canFinish) {
      this.finish$$.emit();
    }
  }

  next(): void {
    if (!this.hasNext || this.isNextDisabled) {
      return;
    }

    this.currentStep.validate().pipe(filter$(Boolean), tap$(() => {
      const nextStepName = this.currentStep.next!;
      const nextStep = this.getStep(nextStepName)!;

      this.path.push(nextStepName);
      this.setCurrentStep(nextStep);
    })).subscribe();
  }

  previous(): void {
    if (!this.hasPrevious || this.isPreviousDisabled) {
      return;
    }

    this.path.pop();
    const previousStepName = last(this.path)!;
    const previousStep = this.getStep(previousStepName)!;
    this.setCurrentStep(previousStep);
  }

  private getStep(name: string): Maybe<WizardStepDirective> {
    return first(this.steps.filter((step) => step.name === name));
  }

  private setCurrentStep(step: WizardStepDirective): void {
    this.currentStep = step;
  }

  private ensureNames(): void {
    const steps = this.steps.toArray();
    const lastIndex = steps.length - 1;

    forEachRight(range(steps.length), (i) => {
      const step = steps[i];

      if (isNil(step.name)) {
        step.name = uniqueId('wizard-step:');
      }

      if (i < lastIndex && isNil(step.next)) {
        step.next = steps[i + 1].name;
      }
    });
  }
}
